function installBootstrapLoader(apiKey: string): void {
  const w = window as unknown as {
    google?: { maps?: Record<string, unknown> };
  };
  const g = (w.google = w.google || {});
  const maps = (g.maps = g.maps || {});
  if (maps.importLibrary) return;

  let scriptLoadPromise: Promise<void> | undefined;
  const requestedLibraries = new Set<string>();
  const readyCallbackName = "__ib__";

  const loadScript = (): Promise<void> =>
    scriptLoadPromise ||
    (scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const params = new URLSearchParams();
      params.set("libraries", [...requestedLibraries].join(","));
      params.set("key", apiKey);
      params.set("v", "weekly");
      params.set("callback", `google.maps.${readyCallbackName}`);

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
      script.async = true;
      (maps as Record<string, unknown>)[readyCallbackName] = resolve;
      script.onerror = () => {
        scriptLoadPromise = undefined;
        reject(new Error("The Google Maps JavaScript API could not load."));
      };
      const existingNonce =
        document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce;
      if (existingNonce) script.nonce = existingNonce;
      document.head.append(script);
    }));

  const importLibraryStub = (library: string, ...rest: unknown[]) => {
    requestedLibraries.add(library);
    return loadScript().then(() => {
      const realImportLibrary = maps.importLibrary as
        | ((...args: unknown[]) => Promise<unknown>)
        | undefined;
      return realImportLibrary?.(library, ...rest);
    });
  };

  maps.importLibrary = importLibraryStub as typeof google.maps.importLibrary;
}

let readyPromise: Promise<typeof google> | null = null;

export function loadGoogleMaps(): Promise<typeof google> {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise((resolve, reject) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set"));
      return;
    }
    installBootstrapLoader(apiKey);
    resolve(window.google);
  });

  return readyPromise;
}

export async function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  const g = await loadGoogleMaps();
  return (await g.maps.importLibrary("places")) as google.maps.PlacesLibrary;
}
